import { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import {
  deleteBugReport,
  fetchBugReports,
  updateBugReport,
} from "services/bugService";
import { BugPriority, BugReport, BugStatus } from "types/bugTypes";

import "./AdminBugList.css";

const STATUS_TABS: { label: string; value: BugStatus | "all" }[] = [
  { label: "All", value: "all" },
  { label: "Open", value: "open" },
  { label: "In Progress", value: "in-progress" },
  { label: "Resolved", value: "resolved" },
  { label: "Closed", value: "closed" },
];

const AdminBugList = () => {
  const [bugs, setBugs] = useState<BugReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<BugStatus | "all">("all");
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Editable fields for the expanded bug
  const [editStatus, setEditStatus] = useState<BugStatus>("open");
  const [editPriority, setEditPriority] = useState<BugPriority>("medium");
  const [editNotes, setEditNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const loadBugs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchBugReports();
      setBugs(data);
    } catch {
      toast.error("Failed to load bug reports.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBugs();
  }, [loadBugs]);

  const filteredBugs =
    statusFilter === "all"
      ? bugs
      : bugs.filter((b) => b.status === statusFilter);

  const statusCounts = bugs.reduce(
    (acc, b) => {
      acc[b.status] = (acc[b.status] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>
  );

  const handleExpand = (bug: BugReport) => {
    if (expandedId === bug.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(bug.id);
    setEditStatus(bug.status);
    setEditPriority(bug.priority);
    setEditNotes(bug.adminNotes);
  };

  const handleSave = async () => {
    if (!expandedId) return;
    setSaving(true);
    try {
      await updateBugReport(expandedId, {
        status: editStatus,
        priority: editPriority,
        adminNotes: editNotes,
      });
      setBugs((prev) =>
        prev.map((b) =>
          b.id === expandedId
            ? { ...b, status: editStatus, priority: editPriority, adminNotes: editNotes }
            : b
        )
      );
      toast.success("Bug updated.");
    } catch {
      toast.error("Failed to update bug.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (bug: BugReport) => {
    if (!window.confirm("Delete this bug report permanently?")) return;
    try {
      await deleteBugReport(bug);
      setBugs((prev) => prev.filter((b) => b.id !== bug.id));
      if (expandedId === bug.id) setExpandedId(null);
      toast.success("Bug deleted.");
    } catch {
      toast.error("Failed to delete bug.");
    }
  };

  const formatDate = (bug: BugReport) => {
    if (!bug.createdAt?.seconds) return "—";
    return new Date(bug.createdAt.seconds * 1000).toLocaleDateString();
  };

  return (
    <div>
      <div className="admin-page-header">
        <div className="admin-page-header__left">
          <h1>Bug Reports</h1>
          {!loading && <span className="admin-badge">{bugs.length}</span>}
        </div>
        <button onClick={loadBugs}>Refresh</button>
      </div>

      {/* Status filter tabs */}
      <div className="admin-tabs">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            className={`admin-tab${statusFilter === tab.value ? " admin-tab--active" : ""}`}
            onClick={() => setStatusFilter(tab.value)}
          >
            {tab.label}
            {tab.value !== "all" && statusCounts[tab.value] ? (
              <span className="admin-tab__count">
                {statusCounts[tab.value]}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {loading && (
        <div className="admin-empty-state">
          <p>Loading bug reports...</p>
        </div>
      )}

      {!loading && filteredBugs.length === 0 && (
        <div className="admin-empty-state">
          <p>No bug reports found.</p>
        </div>
      )}

      {!loading && filteredBugs.length > 0 && (
        <div className="admin-table-wrap">
          <table className="quotation-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Priority</th>
                <th>Description</th>
                <th>Reporter</th>
                <th>Date</th>
                <th style={{ width: 80 }} />
              </tr>
            </thead>
            <tbody>
              {filteredBugs.map((bug) => (
                <BugRow
                  key={bug.id}
                  bug={bug}
                  expanded={expandedId === bug.id}
                  onToggle={() => handleExpand(bug)}
                  onDelete={() => handleDelete(bug)}
                  onSave={handleSave}
                  saving={saving}
                  editStatus={editStatus}
                  setEditStatus={setEditStatus}
                  editPriority={editPriority}
                  setEditPriority={setEditPriority}
                  editNotes={editNotes}
                  setEditNotes={setEditNotes}
                  formatDate={formatDate}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

interface BugRowProps {
  bug: BugReport;
  expanded: boolean;
  onToggle: () => void;
  onDelete: () => void;
  onSave: () => void;
  saving: boolean;
  editStatus: BugStatus;
  setEditStatus: (s: BugStatus) => void;
  editPriority: BugPriority;
  setEditPriority: (p: BugPriority) => void;
  editNotes: string;
  setEditNotes: (n: string) => void;
  formatDate: (bug: BugReport) => string;
}

const BugRow = ({
  bug,
  expanded,
  onToggle,
  onDelete,
  onSave,
  saving,
  editStatus,
  setEditStatus,
  editPriority,
  setEditPriority,
  editNotes,
  setEditNotes,
  formatDate,
}: BugRowProps) => {
  const displayTitle = bug.title || bug.description;

  return (
    <>
      <tr
        className={`bug-row--clickable${expanded ? " bug-row--expanded" : ""}`}
        onClick={onToggle}
      >
        <td>
          <span className={`bug-status-badge bug-status-badge--${bug.status}`}>
            {bug.status.replace("-", " ")}
          </span>
        </td>
        <td>
          <span className={`bug-priority-badge bug-priority-badge--${bug.priority}`}>
            {bug.priority}
          </span>
        </td>
        <td className="bug-desc-cell">{displayTitle}</td>
        <td>{bug.reporterName || bug.reporterEmail || "Anonymous"}</td>
        <td>{formatDate(bug)}</td>
        <td>
          <button
            className="btn-destructive"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
          >
            Delete
          </button>
        </td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={6} style={{ padding: 0 }}>
            <div className="bug-detail">
              <div className="bug-detail__grid">
                {/* Left column: bug info */}
                <div className="bug-detail__section">
                  {bug.title && (
                    <>
                      <h3>Title</h3>
                      <p className="bug-detail__description">{bug.title}</p>
                    </>
                  )}
                  <h3>Description</h3>
                  <p className="bug-detail__description">{bug.description}</p>

                  <p className="bug-detail__meta">
                    <strong>Reporter:</strong>{" "}
                    {bug.reporterName || "Anonymous"}
                    {bug.reporterEmail && ` (${bug.reporterEmail})`}
                  </p>
                  <p className="bug-detail__meta">
                    <strong>Page:</strong> {bug.pageUrl}
                  </p>
                  <p className="bug-detail__meta">
                    <strong>Browser:</strong> {bug.userAgent}
                  </p>
                </div>

                {/* Right column: admin controls */}
                <div className="bug-detail__section">
                  <h3>Manage</h3>
                  <div className="bug-detail__controls">
                    <div className="bug-detail__field">
                      <label>Status</label>
                      <select
                        value={editStatus}
                        onChange={(e) => setEditStatus(e.target.value as BugStatus)}
                      >
                        <option value="open">Open</option>
                        <option value="in-progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Closed</option>
                      </select>
                    </div>

                    <div className="bug-detail__field">
                      <label>Priority</label>
                      <select
                        value={editPriority}
                        onChange={(e) => setEditPriority(e.target.value as BugPriority)}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>

                    <div className="bug-detail__field">
                      <label>Admin Notes</label>
                      <textarea
                        rows={3}
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        placeholder="Internal notes..."
                      />
                    </div>

                    <div className="bug-detail__actions">
                      <button
                        className="btn-primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSave();
                        }}
                        disabled={saving}
                      >
                        {saving ? "Saving..." : "Save"}
                      </button>
                      <button
                        className="btn-destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete();
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default AdminBugList;
